import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { Separator } from '@/Components/ui/separator'
import { cn } from '@/lib/utils'

const dividerVariants = cva('relative flex items-center', {
  variants: {
    variant: {
      default: '',
      withText: '',
    },
    orientation: {
      horizontal: 'w-full',
      vertical: 'h-full',
    },
  },
  defaultVariants: {
    variant: 'default',
    orientation: 'horizontal',
  },
})

const lineVariants = cva('flex-1 border-gray-300', {
  variants: {
    orientation: {
      horizontal: 'border-t',
      vertical: 'border-l',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
  },
})

const textVariants = cva('px-3 text-sm text-gray-500 bg-white', {
  variants: {
    orientation: {
      horizontal: '',
      vertical: 'writing-mode-vertical-rl',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
  },
})

export interface DividerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>, VariantProps<typeof dividerVariants> {
  text?: string
  children?: React.ReactNode
}

const Divider = React.forwardRef<HTMLDivElement, DividerProps>(({ className, variant, orientation, text, children, ...props }, ref) => {
  if (variant === 'withText' && (text || children)) {
    return (
      <div ref={ref} className={cn(dividerVariants({ variant, orientation, className }))} {...props}>
        <div className={cn(lineVariants({ orientation }))} />
        <span className={cn(textVariants({ orientation }))}>{text || children}</span>
        <div className={cn(lineVariants({ orientation }))} />
      </div>
    )
  }

  return (
    <Separator
      ref={ref}
      orientation={orientation || 'horizontal'}
      className={cn(dividerVariants({ variant, orientation, className }))}
      {...props}
    />
  )
})

Divider.displayName = 'Divider'

export { Divider, dividerVariants }
